import React, { useMemo } from 'react';
import Input from 'src/components/primitives/Input/Input';
import { cj, cm } from 'src/utils/classNames';
import { Lightning } from '@drift-labs/icons';
import { SlippageTolerance } from 'src/@types/types';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import { BaseRadioItemProps } from 'src/components/primitives/RadioGroup/types';

interface Props {
	className?: string;
	optionsClassName?: string;
	handleCustomInputChange: (value: string) => void;
	handlePresetClick: (value: SlippageTolerance) => void;
	presetOptions: number[];
	customInputValue: string;
	isCustomInputFocused: boolean;
	handleBlur: () => void;
	handleFocus: () => void;
	currentValue: SlippageTolerance;
	isCurrentDynamic: boolean;
	hideCustom?: boolean;
	hideDynamic?: boolean;
	disabled?: boolean;
}

const NUMBER_FORMAT_PROPS = {
	// Max number is 100, so can limit integer digits to 3.
	maximumIntegerDigits: 3,
	// Beyond 6 and the slippage tolerance gets rendered in scientific notation,
	// which doesn't work with the number field.
	maximumFractionDigits: 6,
};

/**
 * For cases when you want to pass the props in yourself rather than using the
 * `SlippageToleranceInput` / `CollapsibleSlippageToleranceInput` components.
 */
const ControlledSlippageToleranceInput = ({
	className,
	optionsClassName,
	handleCustomInputChange,
	handlePresetClick,
	presetOptions,
	customInputValue,
	isCustomInputFocused,
	handleFocus,
	handleBlur,
	currentValue,
	isCurrentDynamic,
	hideCustom,
	hideDynamic,
	disabled,
}: Props) => {
	const options: BaseRadioItemProps<string>[] = useMemo(
		() => [
			...presetOptions.map((option) => ({
				id: option.toString(),
				label: option.toString() + '%',
				value: option.toString(),
				className: 'flex-1 px-xs',
			})),
			...(!hideDynamic
				? ([
						{
							id: 'dynamic',
							label: <Lightning />,
							value: 'dynamic',
							className: 'flex-1 px-xs',
						},
					] as const)
				: []),
		],
		[presetOptions, hideDynamic]
	);

	return (
		<div className="flex flex-col gap-y-6">
			<div className={cm('flex justify-between gap-x-xs', className)}>
				{!hideCustom && (
					<span className="w-1/3">
						<Input
							labelVariant="none"
							type="number"
							onChange={handleCustomInputChange}
							value={customInputValue === 'dynamic' ? '' : customInputValue}
							suffix={isCurrentDynamic ? '' : '%'}
							disabled={disabled}
							placeholder={
								customInputValue === 'dynamic' && !isCustomInputFocused
									? 'Dynamic'
									: ''
							}
							onFocus={handleFocus}
							onBlur={handleBlur}
							numberFormatProps={NUMBER_FORMAT_PROPS}
						/>
					</span>
				)}
				<RadioGroup.Default
					variant="card"
					className={cj('grow', optionsClassName)}
					size="sm"
					items={options}
					value={currentValue.toString()}
					onValueChange={(value) => {
						handlePresetClick(value === 'dynamic' ? 'dynamic' : Number(value));
					}}
				/>
			</div>
		</div>
	);
};

export default React.memo(ControlledSlippageToleranceInput);
